/* eslint-disable no-fallthrough */
import { makeObservable, observable } from "mobx";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "stores/RootStore";
import type { IRootStore } from "stores/RootStore";
import { LLMClassifierDetails } from "../Views/Wizard/LLMClassifierDetails";
import { LLMClassifierBuilder } from "../Views/Wizard/LLMClassifierBuilder";
import { LLMClassifierTesting } from "../Views/Wizard/LLMClassifierTesting";
import { LLMClassifierVolumeControl } from "../Views/Wizard/LLMClassifierVolumeControl";
import { LLMClassifierVersionHistory } from "../Views/Wizard/LLMClassifierVersionHistory";
import { LLMClassifierBuilderStore, LLMClassifierDefinedOutputsType } from "./LLMClassifierBuilderStore";
import { ChainSourceStore } from "./ChainSourceStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { LLMClassifierPurpose } from "models/LLMs/LLMClassifierPurpose";

export type GeneralWizardStep = {
    /**
     * The firendly name to display in the stepper
     */
    name: string;
    /**
     * Unique identifier for the step. Will be used as a query param in the URL.
     */
    path: string;
    /**
     * The react component to render when this step is active
     */
    component: React.ComponentType;
    /**
     * Give the step the height left in the page so it can scroll its own
     * contents. Unset steps grow with their content.
     */
    fillsHeight?: boolean;
};

const steps = [
    {
        name: "Details",
        path: "details",
        component: LLMClassifierDetails,
    },
    {
        name: "Builder",
        path: "builder",
        component: LLMClassifierBuilder,
    },
    {
        name: "Testing",
        path: "testing",
        component: LLMClassifierTesting,
    },
    {
        name: "Volume Control",
        path: "volume-control",
        component: LLMClassifierVolumeControl,
    },
    {
        name: "Approvals",
        path: "approvals",
        component: LLMClassifierVersionHistory,
        fillsHeight: true,
    },
] as const satisfies readonly GeneralWizardStep[];

export type WizardStep = (typeof steps)[number];
export type WizardPath = WizardStep["path"];

type HasErrorType = {
    nameMissing: boolean;
    moduleMissing: boolean;
    questionMissing: boolean;
    validResultsNotDefined: boolean;
    llmModelMissing: boolean;
    llmPromptTemplateMissing: boolean;
    questionTextMissing: boolean;
    questionDefinitionMissing: boolean;
    criteriaMissing: boolean;
    // chain source errors
    chainSourceRunConditionMissing: boolean;
};

interface IStepsValidator {
    validateDetails(hasError: HasErrorType): boolean;
    validateBuilder(hasError: HasErrorType): boolean;
    validateTesting(hasError: HasErrorType): boolean;
    validateVolumeControl(hasError: HasErrorType): boolean;
}

/**
 * Handles the state for the stepper in model builder and validates the current
 * classifier as it exists in `LLMClassifierBuilderStore`
 */
@AcxStore
export class LLMClassifierBuilderStepperStore extends BaseStore {
    private readonly builderStore: LLMClassifierBuilderStore;
    private readonly chainSourceStore: ChainSourceStore;
    private readonly messageStore: MessageStore;

    private readonly purposeValidators: Record<LLMClassifierPurpose, IStepsValidator>;
        
    @observable currentWizardStep: WizardStep;
    @observable hasError: HasErrorType;

    static readonly WizardSteps = steps;
    static readonly DefaultWizardStep = steps[0];

    public static isValidWizardStepPath(path?: string | null): path is WizardPath {
        return !!path && this.WizardSteps.some((s) => s.path === path);
    }
    public static isValidWizardStep(step?: GeneralWizardStep | null): step is WizardStep {
        return !!step && this.WizardSteps.some((s) => s.path === step.path);
    }

    public static getWizardStepFromPath(path: WizardPath): WizardStep {
        return this.WizardSteps.find((s) => s.path === path)!;
    }

    private static isAdvancing(previousStep: WizardStep, nextStep: WizardStep) {
        return (
            this.WizardSteps.findIndex(s => s.path === nextStep.path) > this.WizardSteps.findIndex(s => s.path === previousStep.path) 
        );
    }

    constructor(private rootStore: IRootStore) {
        super("LLMClassifierBuilderStateStore");
        makeObservable(this);

        this.builderStore = rootStore.getStore(LLMClassifierBuilderStore);
        this.chainSourceStore = rootStore.getStore(ChainSourceStore);
        this.messageStore = rootStore.getStore(MessageStore);

        this.purposeValidators = {
            [LLMClassifierPurpose.Quality]: new QualityPurposeValidator(this.builderStore),
            [LLMClassifierPurpose.Insights]: new InsightsPurposeValidator(this.builderStore),
            [LLMClassifierPurpose.ExtractData]: new ExtractDataPurposeValidator(this.builderStore),
        }

        this.setCurrentWizardStep();
        this.resetHasError();
    }

    /**
     * Sets the current wizard step. 
     * @param step The target step to set as current. If none is provided, the default step will be used.
     * @param force If true, the step will be set ignoring validation. Defaults to `false`
     */
    public setCurrentWizardStep(step?: WizardStep, force?: boolean): void;
    public setCurrentWizardStep(path?: WizardPath, force?: boolean): void;
    public setCurrentWizardStep(stepOrPath?: WizardStep | WizardPath, force: boolean = false): void {
        let step: WizardStep | undefined;
        if (typeof stepOrPath === "string") step = LLMClassifierBuilderStepperStore.getWizardStepFromPath(stepOrPath);
        else step = stepOrPath;

        if (!step) step = LLMClassifierBuilderStepperStore.DefaultWizardStep;
        if (step.path === this.currentWizardStep?.path) return;
        if (!force && !!this.currentWizardStep && LLMClassifierBuilderStepperStore.isAdvancing(this.currentWizardStep, step) && !this.validateStep(this.currentWizardStep)) return;

        this.currentWizardStep = step;

        const url = new URL(window.location.href);
        url.searchParams.set("step", step.path);
        window.history.replaceState(null, "", url);
    }

    public validateStep(step: WizardStep): boolean {
        this.resetHasError();

        const purpose = this.builderStore.classifier.implementation.llmPurpose;
        if (purpose === undefined) return false;
        const validator = this.purposeValidators[purpose];

        switch (step.path) {
            case "volume-control":
                validator.validateVolumeControl(this.hasError);
            case "testing":
                validator.validateTesting(this.hasError);
            case "builder":
                validator.validateBuilder(this.hasError);
                // Every parent's gate needs a value before this model can run,
                // and an output with no published values starts without one.
                this.hasError.chainSourceRunConditionMissing =
                    this.chainSourceStore.hasIncompleteRunConditions;
            case "details":
                validator.validateDetails(this.hasError);
        }

        const isValid = !Object.values(this.hasError).some(Boolean);
        if (!isValid) this.messageStore.logError("Please address all errors before proceeding to the next step");
        return isValid;
    }

    private createHasError(): HasErrorType {
        return {
            nameMissing: false,
            // quality (or insights if linked module selected) errors
            moduleMissing: false,
            questionMissing: false,
            // insights errors,
            validResultsNotDefined: false,
            // both builder errors
            llmModelMissing: false,
            // standard builder errors
            llmPromptTemplateMissing: false,
            criteriaMissing: false,
            questionTextMissing: false,
            questionDefinitionMissing: false,
            // chain source errors
            chainSourceRunConditionMissing: false,
        };
    }

    public resetHasError() {
        this.hasError = this.createHasError();
    }

    public resetError(name: keyof HasErrorType): void {
        this.hasError[name] = false;
    }
}

class QualityPurposeValidator implements IStepsValidator {
    constructor(private readonly builderStore: LLMClassifierBuilderStore) {}

    validateDetails(hasError: HasErrorType): boolean {
        hasError.nameMissing = !this.builderStore.classifier.name;
        if (!this.builderStore.isEditing) {
            hasError.moduleMissing = !this.builderStore.dataLabelingModule;
            if (this.builderStore.dataLabelingModule) {
                hasError.questionMissing = !this.builderStore.dataLabelingQuestion;
            }
        }

        return !Object.values(hasError).some(Boolean);
    }

    validateBuilder(hasError: HasErrorType): boolean {
        hasError.llmModelMissing = !this.builderStore.classifier.implementation.llmModel;
        if (this.builderStore.classifier.implementation.isStandard()) {
            const builder = this.builderStore.classifier.implementation.standardBuilder;
            hasError.criteriaMissing = builder.llmResultDefinitions?.some(rd => !rd.criteria) ?? false;
            // A saved version restores only the template text, not which dropdown option it came from.
            hasError.llmPromptTemplateMissing = !builder.llmPromptTemplate;
            hasError.questionTextMissing = !builder.questionText;
            hasError.questionDefinitionMissing = !builder.questionDefinition;
        }

        return !Object.values(hasError).some(Boolean);
    }

    validateTesting(hasError: HasErrorType): boolean {
        return !Object.values(hasError).some(Boolean);
    }

    validateVolumeControl(hasError: HasErrorType): boolean {
        return !Object.values(hasError).some(Boolean);
    }
}

class InsightsPurposeValidator implements IStepsValidator {
    constructor(private readonly builderStore: LLMClassifierBuilderStore) {}

    validateDetails(hasError: HasErrorType): boolean {
        hasError.nameMissing = !this.builderStore.classifier.name;
        if (this.builderStore.definedOutputsType === LLMClassifierDefinedOutputsType.FromQuestion) {
            hasError.moduleMissing = !this.builderStore.dataLabelingModule;
            if (this.builderStore.dataLabelingModule) {
                hasError.questionMissing = !this.builderStore.dataLabelingQuestion;
            }
        } 
    
        if (this.builderStore.classifier.outputs.length === 0 || this.builderStore.classifier.outputs.some(o => !o.result || o.result.trim() === "")) {
            hasError.validResultsNotDefined = true;
        }

        return !Object.values(hasError).some(Boolean);
    }

    validateBuilder(hasError: HasErrorType): boolean {
        hasError.llmModelMissing = !this.builderStore.classifier.implementation.llmModel;

        return !Object.values(hasError).some(Boolean);
    }

    validateTesting(hasError: HasErrorType): boolean {
        return !Object.values(hasError).some(Boolean);
    }

    validateVolumeControl(hasError: HasErrorType): boolean {
        return !Object.values(hasError).some(Boolean);
    }
}

class ExtractDataPurposeValidator implements IStepsValidator {
    constructor(private readonly builderStore: LLMClassifierBuilderStore) {}

    validateDetails(hasError: HasErrorType): boolean {
        hasError.nameMissing = !this.builderStore.classifier.name;
        return !Object.values(hasError).some(Boolean);
    }

    validateBuilder(hasError: HasErrorType): boolean {
        hasError.llmModelMissing = !this.builderStore.classifier.implementation.llmModel;

        return !Object.values(hasError).some(Boolean);
    }

    validateTesting(hasError: HasErrorType): boolean {
        return !Object.values(hasError).some(Boolean);
    }

    validateVolumeControl(hasError: HasErrorType): boolean {
        return !Object.values(hasError).some(Boolean);
    }
}
